import { X } from 'lucide-react';
import { useIsMobile } from '@/modules/use-window-width';
import { useAiAvailable } from '@/shared/utils/useAiAvailable';
import Panel from './Panel';
import sparkle from './sparkle.png';
import type { AiScope } from '@/shared/utils/useAiOpen';

// On ≤767 the bottom nav's AI pill (FooterMobile) is the entry, so no
// launcher renders there. Self-gated on useAiAvailable because
// DefaultLayout also hosts the public market page.
export default function AiWidget({
  open,
  scope,
  onToggle,
  onClose,
}: {
  open: boolean;
  scope: AiScope;
  onToggle: () => void;
  onClose: () => void;
}) {
  const aiAvailable = useAiAvailable();
  const isMobile = useIsMobile();

  if (!aiAvailable) return null;

  return (
    <>
      <div hidden={!open}>
        {/* A scope change remounts the panel so the previous scope's
            context never bleeds into the new conversation — an answer
            still streaming there is abandoned with it. */}
        <Panel
          key={scope === null ? 'generic' : `${scope.kind}-${scope.signal.id}`}
          open={open}
          scope={scope}
          onClose={onClose}
        />
      </div>
      {!isMobile && (
        <button
          type="button"
          // z-260: above the mobile bottom nav (250), below coach-marks
          // (990) and modals (999); the panel matches it.
          className="fixed right-10 bottom-11 z-260 btn-reset grid size-16 place-items-center rounded-full border border-transparent bg-background-secondary"
          aria-label={open ? 'Close AI assistant' : 'Ask AI'}
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ?
            <X size={28} className="text-gray-text" />
          : <img src={sparkle} alt="" className="size-7 object-cover" />}
          {/* The accent arc is an SVG circle stroke whose only animated
              property is stroke-dashoffset (ai-ring-dash, tailwind.css):
              the geometry never transforms, so the arc stays sharp at any
              device scale — rotating a rasterized ring resamples its
              texture every frame and shimmers. pathLength=360 makes the
              dash pattern read as degrees. The button's visible ring is
              ALSO drawn here (its CSS border is transparent, kept for
              layout only): CSS-border and SVG rasterization misalign at 1x
              displays, fringing seams along the arc body. */}
          <svg
            aria-hidden
            viewBox="0 0 64 64"
            className="absolute -inset-px overflow-visible"
          >
            <circle
              cx={32}
              cy={32}
              r={31.5}
              fill="none"
              stroke="var(--purple-default)"
              strokeWidth={1}
            />
            {!open && (
              <circle
                className="motion-reduce:hidden"
                cx={32}
                cy={32}
                r={31.5}
                fill="none"
                stroke="var(--purple-hover)"
                strokeWidth={1.5}
                strokeLinecap="round"
                pathLength={360}
                strokeDasharray="70 290"
                style={{ animation: 'ai-ring-dash 4s linear infinite' }}
              />
            )}
          </svg>
        </button>
      )}
    </>
  );
}
